import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from '../shared/Icons';
import { formatDateLong, normalizeUtcDate } from './community/utils';
import './NewsBriefing.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://community.ilouli.com';

const VALID_TABS = ['themes', 'audio', 'daily', 'personal'];

const NewsBriefing = () => {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [briefing, setBriefing] = useState(null);
  const [personalBriefing, setPersonalBriefing] = useState(null);
  const [themeBriefing, setThemeBriefing] = useState(null);
  const [audioBriefing, setAudioBriefing] = useState(null);
  const [notebookAudio, setNotebookAudio] = useState(null);  // 현재 선택된 NotebookLM 오디오
  const [allBriefings, setAllBriefings] = useState([]);  // 해당 날짜의 모든 브리핑 (아침+저녁)
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentDialogue, setCurrentDialogue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [themeLoading, setThemeLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);  // NotebookLM 생성 중
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false); // 인증 오류 여부
  const [selectedCategory, setSelectedCategory] = useState('all'); // 카테고리 필터
  const [expandedTheme, setExpandedTheme] = useState(null); // 확장된 테마
  const [themeArticleCount, setThemeArticleCount] = useState({}); // 테마별 표시 기사 수
  const [audioTime, setAudioTime] = useState({ current: 0, duration: 0 });
  const [keywords, setKeywords] = useState([]); // 관심 키워드
  const [newKeyword, setNewKeyword] = useState(''); // 새 키워드 입력
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const audioRef = React.useRef(null);

  // URL search params -> state
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'themes';
  const slotParam = searchParams.get('slot');
  const selectedTimeSlot = slotParam || null;

  // Helper to update search params (preserves other params)
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      // Remove default tab from URL to keep it clean
      if (next.get('tab') === 'themes') next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setActiveTab = useCallback((tab) => {
    updateParams({ tab });
  }, [updateParams]);

  const setSelectedTimeSlot = useCallback((slot) => {
    updateParams({ slot: slot || '' });
  }, [updateParams]);

  // 일일 브리핑 로드
  const fetchBriefing = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAuthError(false);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setBriefing(data.briefing);
      } else {
        // 인증 관련 오류 체크
        const authErrors = ['Invalid token', 'No token provided', 'Token expired', 'Authentication required'];
        if (authErrors.some(e => data.error?.includes(e)) || response.status === 401) {
          setAuthError(true);
          setError('뉴스 브리핑은 로그인한 회원만 이용할 수 있습니다.');
        } else {
          setError(data.error || '브리핑을 불러오는데 실패했습니다.');
        }
      }
    } catch (err) {
      setError('브리핑을 불러오는데 실패했습니다.');
      console.error('Failed to load briefing:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // 개인화 브리핑 로드
  const fetchPersonalBriefing = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/my`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setPersonalBriefing(data);
      }
    } catch (err) {
      console.error('Failed to load personal briefing:', err);
    }
  }, [getToken]);

  // 키워드 로드
  const fetchKeywords = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;

      setKeywordsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/news/keywords`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setKeywords(data.keywords);
      }
    } catch (err) {
      console.error('Failed to load keywords:', err);
    } finally {
      setKeywordsLoading(false);
    }
  }, [getToken]);

  // 키워드 추가
  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ keyword: newKeyword.trim() })
      });

      const data = await response.json();
      if (data.success) {
        setKeywords([...keywords, { id: Date.now(), keyword: newKeyword.trim() }]);
        setNewKeyword('');
        // 키워드 추가 후 개인화 브리핑 새로고침
        setTimeout(() => fetchPersonalBriefing(), 500);
      }
    } catch (err) {
      console.error('Failed to add keyword:', err);
    }
  };

  // 키워드 삭제
  const handleDeleteKeyword = async (keywordId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/keywords/${keywordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setKeywords(keywords.filter(k => k.id !== keywordId));
      }
    } catch (err) {
      console.error('Failed to delete keyword:', err);
    }
  };

  // 테마별 이슈 브리핑 로드
  const fetchThemeBriefing = useCallback(async (refresh = false) => {
    setThemeLoading(true);
    try {
      const params = refresh ? '?refresh=true' : '';
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/themes${params}`);

      const data = await response.json();
      if (data.success) {
        setThemeBriefing(data);
      }
    } catch (err) {
      console.error('Failed to load theme briefing:', err);
    } finally {
      setThemeLoading(false);
    }
  }, []);

  // 오디오 브리핑 (대본) 로드
  const fetchAudioBriefing = useCallback(async () => {
    setAudioLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/audio/script`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.script) {
        setAudioBriefing(data);
      }
    } catch (err) {
      console.error('Failed to load audio briefing:', err);
    } finally {
      setAudioLoading(false);
    }
  }, [getToken]);

  // NotebookLM 오디오 브리핑 로드 (아침 + 저녁 모두)
  const fetchNotebookAudio = useCallback(async () => {
    try {
      const token = getToken();
      // 한국 시간 기준으로 날짜 계산
      const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });

      // 해당 날짜의 모든 브리핑 가져오기
      const response = await fetch(`${API_BASE_URL}/api/news/audio-briefing/${today}/all`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      const data = await response.json();
      if (data.success && data.briefings && data.briefings.length > 0) {
        setAllBriefings(data.briefings);
        // 현재 시간대에 맞는 브리핑 또는 가장 최근 브리핑 선택
        const now = new Date();
        const koreaHour = new Date(now.getTime() + (9 * 60 * 60 * 1000)).getUTCHours();
        const currentSlot = koreaHour >= 12 ? 'evening' : 'morning';

        // 현재 시간대 브리핑이 있으면 선택, 없으면 최신 것
        const currentBriefing = data.briefings.find(b => b.timeSlot === currentSlot) || data.briefings[data.briefings.length - 1];
        setNotebookAudio(currentBriefing);
        setSelectedTimeSlot(currentBriefing?.timeSlot || 'morning');
      } else {
        // 브리핑이 없으면 단일 조회 시도 (생성 중인 경우 포함)
        const singleResponse = await fetch(`${API_BASE_URL}/api/news/audio-briefing/${today}`, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        const singleData = await singleResponse.json();
        if (singleData.success && singleData.briefing) {
          setNotebookAudio(singleData.briefing);
          setSelectedTimeSlot(singleData.briefing?.timeSlot || 'morning');
        }
      }
    } catch (err) {
      console.error('Failed to load NotebookLM audio:', err);
    }
  }, [getToken]);

  // 시간대 선택 변경 핸들러
  const handleTimeSlotChange = (timeSlot) => {
    const briefing = allBriefings.find(b => b.timeSlot === timeSlot);
    if (briefing) {
      // 오디오 일시정지
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      setNotebookAudio(briefing);
      setSelectedTimeSlot(timeSlot);
      setAudioTime({ current: 0, duration: 0 });
    }
  };

  // NotebookLM 오디오 생성 트리거 (Admin)
  const handleGenerateNotebookAudio = async () => {
    if (user?.tier !== 'admin') return;

    setGeneratingAudio(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/notebooklm/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      if (data.success) {
        alert('오디오 브리핑 생성이 시작되었습니다. 15-20분 후 다시 확인해주세요.');
      } else {
        alert(`생성 실패: ${data.error}`);
      }
    } catch (err) {
      console.error('Failed to trigger NotebookLM generation:', err);
      alert('오디오 브리핑 생성 요청에 실패했습니다.');
    } finally {
      setGeneratingAudio(false);
    }
  };

  // 오디오 플레이어 컨트롤
  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setAudioTime({
        current: audioRef.current.currentTime,
        duration: audioRef.current.duration || 0
      });
    }
  };

  const handleAudioSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // TTS 재생 (Web Speech API 사용)
  const playDialogue = useCallback((dialogues, index = 0) => {
    if (!dialogues || index >= dialogues.length) {
      setIsPlaying(false);
      setCurrentDialogue(0);
      return;
    }

    const dialogue = dialogues[index];
    setCurrentDialogue(index);
    setIsPlaying(true);

    const utterance = new SpeechSynthesisUtterance(dialogue.text);
    utterance.lang = 'ko-KR';
    utterance.rate = 0.95;

    // 화자별 음성 톤 구분
    if (dialogue.speaker === 'A') {
      utterance.pitch = 1.1;
    } else {
      utterance.pitch = 0.9;
    }

    utterance.onend = () => {
      playDialogue(dialogues, index + 1);
    };

    window.speechSynthesis.speak(utterance);
  }, []);

  const handlePlayAudio = () => {
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    if (audioBriefing?.script) {
      playDialogue(audioBriefing.script);
    }
  };

  useEffect(() => {
    fetchBriefing();
    fetchThemeBriefing();
    fetchAudioBriefing();
    fetchNotebookAudio();
    // admin 이상만 개인화 브리핑 로드
    if (user?.tier === 'admin') {
      fetchPersonalBriefing();
      fetchKeywords();
    }
  }, [fetchBriefing, fetchPersonalBriefing, fetchThemeBriefing, fetchAudioBriefing, fetchNotebookAudio, fetchKeywords, user?.tier]);

  // 브리핑 재생성 (Admin)
  const handleRegenerate = async () => {
    if (user?.tier !== 'admin') return;

    setGenerating(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setBriefing(data.briefing);
      }
    } catch (err) {
      console.error('Failed to regenerate briefing:', err);
    } finally {
      setGenerating(false);
    }
  };

  // formatDate: community/utils.js에서 import
  const formatDate = formatDateLong;

  // 시간 포맷
  const formatTime = (dateString) => {
    const date = new Date(normalizeUtcDate(dateString));
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Seoul'
    });
  };

  if (loading) {
    return (
      <div className="news-briefing">
        <div className="briefing-loading">
          <div className="spinner"></div>
          <p>브리핑을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-briefing">
        <div className="briefing-error">
          <Icon name={authError ? "lock" : "warning"} size={48} className="error-icon" />
          <p>{error}</p>
          {authError ? (
            <a href="https://ilouli.com/login" className="login-btn">로그인하기</a>
          ) : (
            <button onClick={fetchBriefing}>다시 시도</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="news-briefing">
      {/* 헤더 */}
      <div className="briefing-header">
        <div className="header-content">
          <h1>오늘의 뉴스 브리핑</h1>
          <p className="briefing-date">
            {briefing?.date ? formatDate(briefing.date) : ''}
          </p>
        </div>
        <div className="header-actions">
          {briefing?.generatedAt && (
            <span className="generated-time">
              마지막 업데이트: {formatTime(briefing.generatedAt)}
            </span>
          )}
          {user?.tier === 'admin' && (
            <button
              className="regenerate-btn"
              onClick={handleRegenerate}
              disabled={generating}
            >
              {generating ? '생성 중...' : <><Icon name="refresh" size={14} /> 재생성</>}
            </button>
          )}
        </div>
      </div>

      {/* 탭 */}
      <div className="briefing-tabs">
        <button
          className={`tab-btn ${activeTab === 'themes' ? 'active' : ''}`}
          onClick={() => setActiveTab('themes')}
        >
          <Icon name="fire" size={14} /> 테마별 이슈
        </button>
        <button
          className={`tab-btn ${activeTab === 'audio' ? 'active' : ''}`}
          onClick={() => setActiveTab('audio')}
        >
          <Icon name="play" size={14} /> 오디오 브리핑
        </button>
        <button
          className={`tab-btn ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          <Icon name="chart" size={14} /> 일일 브리핑
        </button>
        {user?.tier === 'admin' && (
          <button
            className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            <Icon name="star" size={14} /> 내 관심 브리핑
          </button>
        )}
      </div>

      {/* 테마별 이슈 탭 */}
      {activeTab === 'themes' && (
        <div className="briefing-content themes-content">
          <div className="themes-header">
            <p className="themes-description">
              AI가 오늘의 핫 이슈를 테마별로 분석했습니다
            </p>
            <button
              className="refresh-themes-btn"
              onClick={() => fetchThemeBriefing(true)}
              disabled={themeLoading}
            >
              {themeLoading ? '분석 중...' : <><Icon name="refresh" size={14} /> 새로고침</>}
            </button>
          </div>

          {/* AI 핵심 요약 */}
          {themeBriefing?.overallSummary && themeBriefing.overallSummary.length > 0 && (
            <section className="briefing-section ai-report-section">
              <h2><Icon name="chart" size={18} /> 오늘의 핵심 요약</h2>
              <ul className="summary-list">
                {themeBriefing.overallSummary.map((item, idx) => (
                  <li key={idx} className="summary-item">
                    <span className="summary-number">{idx + 1}</span>
                    <span className="summary-text">{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* 카테고리 탭 */}
          <div className="category-tabs">
            <button
              className={`category-tab ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => { setSelectedCategory('all'); setExpandedTheme(null); }}
            >
              <span className="tab-name">전체</span>
              <span className="tab-count">{themeBriefing?.themes?.length || 0}</span>
            </button>
            {themeBriefing?.categories?.map(cat => {
              const themeCount = themeBriefing?.themes?.filter(t => t.category === cat.code).length || 0;
              if (themeCount === 0) return null;
              return (
                <button
                  key={cat.code}
                  className={`category-tab ${selectedCategory === cat.code ? 'active' : ''}`}
                  onClick={() => { setSelectedCategory(cat.code); setExpandedTheme(null); }}
                >
                  <span className="tab-icon">{cat.icon}</span>
                  <span className="tab-name">{cat.name}</span>
                  <span className="tab-count">{themeCount}</span>
                </button>
              );
            })}
          </div>

          {/* 생성 중 배너 */}
          {themeBriefing?.generating && (
            <div className="generating-banner">
              <div className="generating-animation inline">
                <span className="wave"></span>
                <span className="wave"></span>
                <span className="wave"></span>
              </div>
              <span className="generating-text">
                {themeBriefing.isYesterdayData
                  ? `오늘의 테마 브리핑을 준비 중입니다. (아래는 ${themeBriefing.actualDate} 데이터)`
                  : '테마 브리핑을 생성 중입니다...'}
              </span>
              <button onClick={() => fetchThemeBriefing(false)} className="mini-refresh-btn">
                <Icon name="refresh" size={12} />
              </button>
            </div>
          )}

          {themeLoading ? (
            <div className="theme-loading">
              <div className="spinner"></div>
              <p>AI가 뉴스를 분석 중입니다...</p>
            </div>
          ) : themeBriefing?.themes && themeBriefing.themes.length > 0 ? (
            <>
              <div className="theme-issues-grid">
                {themeBriefing.themes
                  .filter(theme => selectedCategory === 'all' || theme.category === selectedCategory)
                  .map((theme, idx) => {
                    const currentCount = themeArticleCount[theme.keyword] || 3;
                    const totalArticles = theme.relatedArticles.length;
                    const displayArticles = theme.relatedArticles.slice(0, currentCount);
                    const hasMore = currentCount < totalArticles;
                    const remainingCount = totalArticles - currentCount;

                    const handleShowMore = () => {
                      setThemeArticleCount(prev => ({
                        ...prev,
                        [theme.keyword]: Math.min(currentCount + 5, totalArticles)
                      }));
                    };

                    const handleShowAll = () => {
                      setThemeArticleCount(prev => ({
                        ...prev,
                        [theme.keyword]: totalArticles
                      }));
                    };

                    const handleCollapse = () => {
                      setThemeArticleCount(prev => ({
                        ...prev,
                        [theme.keyword]: 3
                      }));
                    };

                    return (
                      <div
                        key={idx}
                        className={`theme-issue-card ${currentCount > 3 ? 'expanded' : ''}`}
                      >
                        <div className="theme-issue-header">
                          <span className="theme-rank">#{idx + 1}</span>
                          <span className="theme-keyword-title">{theme.keyword}</span>
                          <span className="theme-article-count">{theme.articleCount}건</span>
                          {theme.categoryIcon && (
                            <span className="theme-category-badge" title={theme.categoryName}>
                              {theme.categoryIcon}
                            </span>
                          )}
                        </div>
                        <p className="theme-issue-summary">{theme.issueSummary}</p>
                        <div className="theme-related-articles expanded">
                          {displayArticles.map((article, aIdx) => (
                            <a
                              key={aIdx}
                              href={article.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="theme-article-link"
                            >
                              <span className={`mini-badge ${article.category}`}>{article.categoryLabel}</span>
                              <span className="article-link-title">{article.title}</span>
                            </a>
                          ))}
                        </div>
                        {totalArticles > 3 && (
                          <div className="article-actions">
                            {hasMore ? (
                              <>
                                <button className="show-more-btn" onClick={handleShowMore}>
                                  +5개 더보기 ({remainingCount}개 남음)
                                  <Icon name="chevronDown" size={14} />
                                </button>
                                <button className="show-all-btn" onClick={handleShowAll}>
                                  전체보기 ({totalArticles})
                                </button>
                              </>
                            ) : (
                              <button className="collapse-btn" onClick={handleCollapse}>
                                접기 <Icon name="chevronUp" size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              <div className="themes-stats">
                <span>총 {themeBriefing.totalArticles}개 기사에서 {themeBriefing.totalThemes}개 테마 분석</span>
                {themeBriefing.generatedAt && (
                  <span className="themes-time">
                    {formatTime(themeBriefing.generatedAt)} 기준
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="empty-themes">
              <Icon name="fire" size={48} className="empty-icon" />
              <h3>분석할 뉴스가 없습니다</h3>
              <p>최근 24시간 내 수집된 뉴스가 부족합니다.</p>
            </div>
          )}
        </div>
      )}

      {/* 오디오 브리핑 탭 */}
      {activeTab === 'audio' && (
        <div className="briefing-content audio-content">
          {/* 시간대 선택 탭 (아침/저녁 브리핑이 모두 있을 때) */}
          {allBriefings.length > 1 && (
            <div className="time-slot-tabs">
              {allBriefings.map(b => (
                <button
                  key={b.timeSlot}
                  className={`time-slot-tab ${selectedTimeSlot === b.timeSlot ? 'active' : ''}`}
                  onClick={() => handleTimeSlotChange(b.timeSlot)}
                >
                  {b.timeSlot === 'evening' ? '🌙 저녁 브리핑' : '☀️ 아침 브리핑'}
                </button>
              ))}
            </div>
          )}

          {/* NotebookLM 오디오 플레이어 (있는 경우) */}
          {notebookAudio && notebookAudio.audioUrl && notebookAudio.status === 'completed' ? (
            <div className="notebooklm-player">
              <div className="player-header">
                <div className="player-info">
                  <h2>📻 {notebookAudio.timeSlot === 'evening' ? '저녁' : '아침'} 뉴스 브리핑</h2>
                  <p className="player-meta">
                    <span className={`time-slot-badge ${notebookAudio.timeSlot || 'morning'}`}>
                      {notebookAudio.timeSlot === 'evening' ? '🌙 저녁 브리핑' : '☀️ 아침 브리핑'}
                    </span>
                    {notebookAudio.date && (
                      <span className="player-date">{formatDate(notebookAudio.date)}</span>
                    )}
                  </p>
                </div>
                {user?.tier === 'admin' && (
                  <button
                    className="regenerate-audio-btn"
                    onClick={handleGenerateNotebookAudio}
                    disabled={generatingAudio}
                  >
                    {generatingAudio ? '생성 중...' : <><Icon name="refresh" size={14} /> 재생성</>}
                  </button>
                )}
              </div>

              <div className="player-controls">
                <audio
                  ref={audioRef}
                  src={`${API_BASE_URL}${notebookAudio.audioUrl}`}
                  onTimeUpdate={handleAudioTimeUpdate}
                  onEnded={() => setIsPlaying(false)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />

                <button
                  className={`play-pause-btn ${isPlaying ? 'playing' : ''}`}
                  onClick={handleAudioPlay}
                >
                  {isPlaying ? <Icon name="pause" size={24} /> : <Icon name="play" size={24} />}
                </button>

                <div className="progress-container" onClick={handleAudioSeek}>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: audioTime.duration ? `${(audioTime.current / audioTime.duration) * 100}%` : '0%'
                      }}
                    />
                  </div>
                </div>

                <div className="time-display">
                  <span>{formatAudioTime(audioTime.current)}</span>
                  <span>/</span>
                  <span>{formatAudioTime(audioTime.duration)}</span>
                </div>
              </div>

              <div className="player-footer">
                <p className="player-description">
                  AI가 생성한 두 사람의 자연스러운 대화 형식 팟캐스트입니다.
                  {notebookAudio.fileSize && (
                    <span className="file-size"> ({Math.round(notebookAudio.fileSize / 1024 / 1024 * 10) / 10}MB)</span>
                  )}
                </p>
              </div>

              {/* 브리핑 대본 표시 */}
              {notebookAudio.script && (
                <div className="briefing-script-section">
                  <h3>📝 브리핑 대본</h3>
                  <div className="script-content">
                    <pre className="script-text">{notebookAudio.script}</pre>
                  </div>
                </div>
              )}
            </div>
          ) : notebookAudio && notebookAudio.status === 'generating' ? (
            <div className="audio-generating">
              <div className="generating-animation">
                <span className="wave"></span>
                <span className="wave"></span>
                <span className="wave"></span>
              </div>
              <h3>오디오 브리핑 생성 중...</h3>
              <p>AI가 팟캐스트를 만들고 있습니다. 15-20분 정도 소요됩니다.</p>
              <button onClick={fetchNotebookAudio} className="refresh-btn">
                <Icon name="refresh" size={14} /> 상태 확인
              </button>
            </div>
          ) : (
            <>
              {/* 오디오가 없을 때 기존 TTS 대본 표시 */}
              <div className="audio-header">
                <div className="audio-info">
                  <h2><Icon name="play" size={20} /> AI 뉴스 대담</h2>
                  <p>두 AI가 오늘의 뉴스에 대해 대화합니다</p>
                </div>
                <div className="audio-actions">
                  {user?.tier === 'admin' && (
                    <button
                      className="generate-audio-btn"
                      onClick={handleGenerateNotebookAudio}
                      disabled={generatingAudio}
                    >
                      {generatingAudio ? '생성 중...' : <><Icon name="plus" size={14} /> 팟캐스트 생성</>}
                    </button>
                  )}
                  <button
                    className={`audio-play-btn ${isPlaying ? 'playing' : ''}`}
                    onClick={handlePlayAudio}
                    disabled={audioLoading || !audioBriefing?.script}
                  >
                    {isPlaying ? (
                      <><Icon name="pause" size={18} /> 정지</>
                    ) : (
                      <><Icon name="play" size={18} /> TTS 재생</>
                    )}
                  </button>
                </div>
              </div>

              {audioLoading ? (
                <div className="audio-loading">
                  <div className="spinner"></div>
                  <p>대본을 불러오는 중...</p>
                </div>
              ) : audioBriefing?.script && audioBriefing.script.length > 0 ? (
                <div className="dialogue-container">
                  <div className="dialogue-list">
                    {audioBriefing.script.map((dialogue, idx) => (
                      <div
                        key={idx}
                        className={`dialogue-item speaker-${dialogue.speaker} ${currentDialogue === idx && isPlaying ? 'active' : ''}`}
                      >
                        <div className="dialogue-speaker">
                          <span className={`speaker-avatar ${dialogue.speaker}`}>
                            {dialogue.speaker === 'A' ? '🎙️' : '🎧'}
                          </span>
                          <span className="speaker-name">
                            {dialogue.speaker === 'A' ? '진행자 A' : '진행자 B'}
                          </span>
                        </div>
                        <div className="dialogue-text">{dialogue.text}</div>
                      </div>
                    ))}
                  </div>
                  {audioBriefing.date && (
                    <div className="audio-date">
                      {formatDate(audioBriefing.date)} 브리핑
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-audio">
                  <Icon name="play" size={48} className="empty-icon" />
                  <h3>오디오 브리핑이 없습니다</h3>
                  <p>아직 생성된 오디오가 없습니다.</p>
                  {user?.tier === 'admin' && (
                    <button
                      onClick={handleGenerateNotebookAudio}
                      className="generate-btn"
                      disabled={generatingAudio}
                    >
                      {generatingAudio ? '생성 요청 중...' : <><Icon name="plus" size={14} /> 팟캐스트 생성</>}
                    </button>
                  )}
                  <button onClick={fetchAudioBriefing} className="retry-btn">
                    <Icon name="refresh" size={14} /> 다시 시도
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'daily' && briefing && (
        <div className="briefing-content">
          {/* AI Insight (1000자+) */}
          {briefing.aiInsight && (
            <section className="briefing-section ai-insight-section">
              <h2><Icon name="chart" size={18} /> AI 시장 분석 리포트</h2>
              <div className="ai-insight-content">
                <pre className="ai-insight-text">{briefing.aiInsight}</pre>
              </div>
            </section>
          )}

          {/* 핵심 요약 */}
          <section className="briefing-section summary-section">
            <h2>오늘의 핵심 요약</h2>
            {briefing.summary && briefing.summary.length > 0 ? (
              <ul className="summary-list">
                {briefing.summary.map((point, idx) => (
                  <li key={idx} className="summary-item">
                    <span className="summary-number">{idx + 1}</span>
                    <span className="summary-text">{point}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty-message">오늘 수집된 뉴스가 없습니다.</p>
            )}
          </section>

          {/* 핫 키워드 */}
          <section className="briefing-section keywords-section">
            <h2><Icon name="fire" size={18} /> 핫 키워드</h2>
            {briefing.hotKeywords && briefing.hotKeywords.length > 0 ? (
              <div className="keywords-cloud">
                {briefing.hotKeywords.map((kw, idx) => (
                  <span
                    key={idx}
                    className={`keyword-tag size-${Math.min(kw.count, 5)}`}
                  >
                    #{kw.word}
                    <span className="keyword-count">{kw.count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="empty-message">키워드가 없습니다.</p>
            )}
          </section>

          {/* 섹터별 동향 */}
          <section className="briefing-section sector-section">
            <h2><Icon name="trendingUp" size={18} /> 섹터별 동향</h2>
            {briefing.sectorTrend && Object.keys(briefing.sectorTrend).length > 0 ? (
              <div className="sector-grid">
                {Object.entries(briefing.sectorTrend).map(([sector, data]) => (
                  <div key={sector} className={`sector-card status-${data.status}`}>
                    <span className="sector-name">{sector}</span>
                    <span className="sector-status">{data.label}</span>
                    <span className="sector-count">{data.count}건</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">섹터 데이터가 없습니다.</p>
            )}
          </section>

          {/* 카테고리별 기사 수 */}
          {briefing.categoryBreakdown && briefing.categoryBreakdown.length > 0 && (
            <section className="briefing-section category-section">
              <h2><Icon name="folderOpen" size={18} /> 카테고리별 현황</h2>
              <div className="category-bars">
                {briefing.categoryBreakdown.map((cat, idx) => {
                  const maxCount = Math.max(...briefing.categoryBreakdown.map(c => c.count));
                  const width = (cat.count / maxCount) * 100;
                  return (
                    <div key={idx} className="category-bar-item">
                      <span className="category-label">{cat.label}</span>
                      <div className="category-bar">
                        <div className="category-bar-fill" style={{ width: `${width}%` }}></div>
                      </div>
                      <span className="category-count">{cat.count}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 통계 */}
          <div className="briefing-stats">
            <span className="stat-item">
              <Icon name="news" size={16} className="stat-icon" />
              총 {briefing.totalArticles}개 기사 분석
            </span>
          </div>
        </div>
      )}

      {activeTab === 'personal' && user?.tier === 'admin' && (
        <div className="briefing-content personal-content">
          {/* 키워드 관리 섹션 */}
          <section className="briefing-section keywords-manage-section">
            <h2><Icon name="star" size={18} /> 관심 키워드 관리</h2>
            <p className="section-desc">관심 키워드를 등록하면 해당 키워드와 관련된 뉴스를 모아서 AI 분석 리포트를 제공합니다.</p>
            <div className="keyword-input-row">
              <input
                type="text"
                placeholder="키워드 추가..."
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
              />
              <button onClick={handleAddKeyword} disabled={!newKeyword.trim()}>추가</button>
            </div>
            <div className="keywords-list">
              {keywordsLoading ? (
                <span className="keywords-loading">로딩 중...</span>
              ) : keywords.length > 0 ? (
                keywords.map(kw => (
                  <span key={kw.id} className="keyword-tag">
                    {kw.keyword}
                    <button onClick={() => handleDeleteKeyword(kw.id)}>×</button>
                  </span>
                ))
              ) : (
                <span className="no-keywords">등록된 키워드가 없습니다</span>
              )}
            </div>
          </section>

          {personalBriefing?.results && personalBriefing.results.length > 0 ? (
            <>
              {/* AI Insight (1000자+) */}
              {personalBriefing.aiInsight && (
                <section className="briefing-section ai-insight-section personal-insight">
                  <h2><Icon name="chart" size={18} /> 관심 분야 AI 분석 리포트</h2>
                  <div className="ai-insight-keywords">
                    {personalBriefing.keywords?.map((kw, idx) => (
                      <span key={idx} className="insight-keyword-tag">#{kw}</span>
                    ))}
                  </div>
                  <div className="ai-insight-content">
                    <pre className="ai-insight-text">{personalBriefing.aiInsight}</pre>
                  </div>
                  {personalBriefing.totalArticles && (
                    <div className="insight-stats">
                      총 {personalBriefing.totalArticles}개 관련 기사 분석
                    </div>
                  )}
                </section>
              )}

              {/* 키워드별 기사 목록 */}
              {personalBriefing.results.map((result, idx) => (
                <section key={idx} className="briefing-section keyword-result-section">
                  <h3>
                    <span className="keyword-badge">"{result.keyword}"</span>
                    <span className="result-count">{result.count}개 기사</span>
                  </h3>
                  <ul className="keyword-articles">
                    {result.articles.map((article, aIdx) => (
                      <li key={aIdx} className="keyword-article">
                        <a href={article.link} target="_blank" rel="noopener noreferrer">
                          {article.title}
                        </a>
                        <span className="article-source">{article.source}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </>
          ) : keywords.length > 0 ? (
            <div className="empty-personal">
              <Icon name="chart" size={48} className="empty-icon" />
              <h3>키워드 관련 뉴스를 분석 중...</h3>
              <p>등록된 키워드와 관련된 뉴스가 수집되면<br/>AI 분석 리포트가 생성됩니다.</p>
              <button onClick={fetchPersonalBriefing} className="refresh-btn">
                <Icon name="refresh" size={14} /> 새로고침
              </button>
            </div>
          ) : (
            <div className="empty-personal">
              <Icon name="star" size={48} className="empty-icon" />
              <h3>관심 키워드를 등록해주세요</h3>
              <p>위에서 관심 키워드를 등록하면<br/>맞춤 브리핑을 받아볼 수 있습니다.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsBriefing;
